import { supabase } from '@/integrations/supabase/client';

export interface SlackMentionCandidate {
  kind: 'member';
  id: string;
  name: string;
}

/** A channel result — distinct from a member so the dropdown can render a
 *  `#` hash icon instead of an identity dot, and so selecting it inserts
 *  `#channel-name` plain text instead of an `@`-mention node (see
 *  `SlackMentionList`'s `selectItem`) — that's the shape `send_to_slack`'s
 *  `channelName` input already documents ("march-media" or "#approvals"). */
export interface SlackChannelMentionCandidate {
  kind: 'channel';
  id: string;
  name: string;
  isPrivate: boolean;
}

/** Rendered as a single CTA row instead of the normal member list when the
 *  current workspace has no active Slack connection at all — see the doc
 *  comment on `fetchSlackMentions` below for why this exists as an item in
 *  the array rather than a separate flag. */
export interface SlackConnectPrompt {
  kind: 'connect-prompt';
}

export type SlackMentionResult =
  SlackMentionCandidate | SlackChannelMentionCandidate | SlackConnectPrompt;

/**
 * Client-side fetch backing the Home composer's `@`-mention autocomplete
 * (`slack-mention-suggestion.tsx`) — calls `GET /api/slack/mentions`, which
 * resolves the CURRENT workspace's Slack connection server-side the same way
 * `send_to_slack` does. Lives under `src/lib/` (not `src/components/`)
 * specifically so it may hold the direct Supabase import needed for
 * `auth.getSession()` — this repo's "no direct Supabase imports in
 * components" lint rule is scoped to `src/components/**`/`src/pages/**`.
 *
 * Distinguishes "no Slack connection at all" from "connected, zero matches"
 * via the API's `connected` flag — founder feedback, live: silently
 * returning an empty list for a workspace that was never connected reads as
 * a broken search ("it says Searching Slack… then nothing"), not as "there's
 * nothing to search here." A one-item `connect-prompt` result surfaces that
 * distinction WITHOUT adding a second return shape tiptap's `items()` isn't
 * built to carry (it wants a plain array of whatever type `T` the caller
 * picks — `SlackMentionResult` folds the two cases into that one type
 * instead of returning `{ items, connected }`).
 *
 * Still degrades to a genuinely empty list on transport failure (no session,
 * non-ok response, network error, or an aborted/superseded request) — those
 * are "couldn't check," not "checked and it's not connected," and get no
 * CTA since guessing which one applies would be a real error disguised as
 * an onboarding nudge.
 */
export async function fetchSlackMentions(
  query: string,
  signal: AbortSignal,
): Promise<SlackMentionResult[]> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) return [];

  try {
    const response = await fetch(`/api/slack/mentions?q=${encodeURIComponent(query)}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    if (!response.ok) return [];
    const body = (await response.json().catch(() => null)) as {
      members?: Array<{ id: string; name: string }>;
      channels?: Array<{ id: string; name: string; isPrivate: boolean }>;
      connected?: boolean;
    } | null;
    if (!body) return [];
    if (!body.connected) return [{ kind: 'connect-prompt' }];
    const members = (body.members ?? []).map((member) => ({ kind: 'member' as const, ...member }));
    const channels = (body.channels ?? []).map((channel) => ({
      kind: 'channel' as const,
      ...channel,
    }));
    return [...members, ...channels];
  } catch {
    return [];
  }
}
